import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { create } from "zustand";
import { flattenRailLayout, normaliseRailLayout, type MyCommunity, type RailItem } from "@atrium/shared";
import { api, errorMessage } from "../../lib/api";
import { useMyCommunities } from "../../lib/hooks";
import { queryClient } from "../../lib/queryClient";
import { toast } from "../../components/ui/Toast";

/*
 * The rail layout (order + folders) lives on the server (GET/PUT /api/me/rail)
 * and is reconciled with the communities you're in on every render, so new
 * joins are appended and left communities disappear without a refetch.
 */

export const RAIL_KEY = ["rail"] as const;

export type RailNode =
  | { kind: "community"; id: string; community: MyCommunity }
  | { kind: "folder"; id: string; folder: Extract<RailItem, { kind: "folder" }>; communities: MyCommunity[] };

export function useRail() {
  const communities = useMyCommunities();
  const saved = useQuery({ queryKey: RAIL_KEY, queryFn: () => api.get<{ items: RailItem[] }>("/api/me/rail").then((r) => r.items), staleTime: 5 * 60_000 });
  const list = communities.data;
  const layout = useMemo(() => (list ? normaliseRailLayout(saved.data ?? [], list.map((c) => c.id)) : []), [list, saved.data]);
  const nodes = useMemo<RailNode[]>(() => {
    if (!list) return [];
    const byId = new Map(list.map((c) => [c.id, c]));
    return layout.map((it) =>
      it.kind === "community"
        ? { kind: "community", id: it.id, community: byId.get(it.id)! }
        : { kind: "folder", id: it.id, folder: it, communities: it.communityIds.map((id) => byId.get(id)!) },
    );
  }, [layout, list]);
  return {
    layout,
    nodes,
    communities: list,
    isPending: communities.isPending || (saved.isPending && !saved.isError),
  };
}

/* Saving: optimistic, serialised, and rolled back to the last confirmed layout on failure. */

let chain: Promise<unknown> = Promise.resolve();
let confirmed: RailItem[] | undefined;
let pending = 0;
let seq = 0;

function applyLocally(items: RailItem[]) {
  queryClient.setQueryData(RAIL_KEY, items);
  // Keep the flat community list (mobile list, palette) in the same order.
  const order = new Map(flattenRailLayout(items).map((id, i) => [id, i]));
  queryClient.setQueryData<MyCommunity[]>(["my-communities"], (old) => old && [...old].sort((a, b) => (order.get(a.id) ?? 1e9) - (order.get(b.id) ?? 1e9)));
}

export function saveRail(next: RailItem[], opts: { error?: string } = {}) {
  const ids = queryClient.getQueryData<MyCommunity[]>(["my-communities"])?.map((c) => c.id) ?? [];
  const items = normaliseRailLayout(next, ids);
  if (pending === 0) confirmed = queryClient.getQueryData<RailItem[]>(RAIL_KEY) ?? [];
  void queryClient.cancelQueries({ queryKey: RAIL_KEY });
  applyLocally(items);
  pending++;
  const mine = ++seq;
  chain = chain
    .then(() => api.put<{ items: RailItem[] }>("/api/me/rail", { items }))
    .then(
      (r) => {
        confirmed = r.items;
        if (mine === seq) queryClient.setQueryData(RAIL_KEY, r.items);
      },
      (e) => {
        if (mine === seq) {
          applyLocally(confirmed ?? []);
          toast.error(errorMessage(e, opts.error ?? "Couldn't save your community order"));
        }
      },
    )
    .finally(() => {
      pending--;
    });
}

/** Current layout from the cache, reconciled (for actions outside React render). */
export function currentRail(): RailItem[] {
  const ids = queryClient.getQueryData<MyCommunity[]>(["my-communities"])?.map((c) => c.id) ?? [];
  return normaliseRailLayout(queryClient.getQueryData<RailItem[]>(RAIL_KEY) ?? [], ids);
}

/* Which folders are open is per device. */

const OPEN_KEY = "atrium:rail-open-folders";
const readOpen = (): Record<string, true> => {
  try {
    return JSON.parse(localStorage.getItem(OPEN_KEY) ?? "{}") ?? {};
  } catch {
    return {};
  }
};

export const useRailUI = create<{
  open: Record<string, true>;
  setOpen: (id: string, open: boolean) => void;
  /** Folder whose settings dialog is showing. */
  editing: string | null;
  setEditing: (id: string | null) => void;
}>((set, get) => ({
  open: readOpen(),
  setOpen: (id, open) => {
    const next = { ...get().open };
    if (open) next[id] = true;
    else delete next[id];
    set({ open: next });
    try {
      localStorage.setItem(OPEN_KEY, JSON.stringify(next));
    } catch {
      /* storage full or blocked */
    }
  },
  editing: null,
  setEditing: (editing) => set({ editing }),
}));

/** Default folder name: its communities' names. */
export const folderLabel = (name: string, communities: { name: string }[]) =>
  name.trim() || communities.map((c) => c.name).join(", ") || "Folder";
